import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Volume2, VolumeX, Compass, MapPin } from "lucide-react";
import { useSound } from "@/context/SoundContext";
import WildRushWorld from "./WildRushWorld";
import { SCREENS, FIRST_SCREEN, BLOCK_1_STAGE, PLAYERS, isValidTransition } from "./screens";
import { readStoredSession, clearStoredSession, startAttempt, loadAttempt, saveState, appendEvent, trackStage, recordCompletion, buildReport } from "./attemptClient";
import { track } from "@/lib/analytics";
import { PracticeReportV2 } from "@/components/PracticeReportV2";
import PhaseDStage from "./PhaseDStage";
import IntroScreen from "./IntroScreen";
import { WildRushHome } from "./WildRushHome";
import { SafeguardingClose } from "./Sheets";
import { D, BLOCK_2_STAGE, BLOCK_3_STAGE, OPTION_B_THRESHOLD, FINAL_CONTACT_SCREEN, isContactScreen } from "./phaseD";
import SupportStage from "./SupportStage";
import ReflectionStage from "./ReflectionStage";
import { REFLECTION_START, REFLECTION_END, BLOCK_5_STAGE, COMPLETE_SCREEN } from "./phaseF";
import { E, BLOCK_4_STAGE, BLOCK_4_ENTRIES, SUPPORT_SCREENS, PHASE_E_ENTRY, NORMALITY_SCREEN, END_SCREEN } from "./phaseE";
// Branch-table handoff reasons resolve to live screens (Phase E wiring of the former preview boundaries).
const HANDOFF_ROUTES = { phase_e: PHASE_E_ENTRY, screen_29: 32, contact_ended: NORMALITY_SCREEN };
const ENGAGE_EVENTS = new Set(["contact_replied", "co_play_started", "friend_request_accepted", "private_chat_entered"]);

const ACCENT = "#4FA3D9";

// Simulation #2 orchestrator: Block 1 (Screens 1–8) → contact & branching (PhaseDStage) → protection/help/recovery (SupportStage) → reflection & completion (ReflectionStage) → Practice Report (PracticeReportV2).
// screen definition -> WildRushWorld + HUD + authored feed -> action -> state patch -> event -> next screen.
export default function SomeoneIMetOnlineExperience({ sim }) {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const { enabled, toggle, unlock, setAmbientScene, chime, click } = useSound();
  const [sid, setSid] = useState(null);
  const [screen, setScreen] = useState(null); // null = booting
  const [state, setState] = useState({});
  const [feed, setFeed] = useState([]);
  const [busy, setBusy] = useState(false);
  const booted = useRef(false);
  const stageSent = useRef(false);
  // Phase D runtime memory (derived from bounded events on resume; never stored as raw state).
  const ignores = useRef(0);
  const finalShown = useRef(false);
  const returnTo = useRef(null);
  const stages = useRef(new Set());
  const [report, setReport] = useState(null); // Phase F4: frozen Practice Report (schema v2) for this attempt
  const [intro, setIntro] = useState(false); // fresh entry (no resumable attempt) → introduction before Screen 1
  const pvSent = useRef(false);
  const replayNext = useRef(false); // set by Practise Again so the next BEGIN emits simulation_restart

  // Canonical simulation_page_view once per page open (same semantics as Sim #1's experience shell).
  useEffect(() => {
    if (pvSent.current) return;
    pvSent.current = true;
    track("simulation_page_view", null, { simulation_id: sim.simulationId });
  }, [sim.simulationId]);

  // Boot: resume a stored sim_000002 attempt after refresh/interruption, else wait for Enter.
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    (async () => {
      const stored = readStoredSession();
      if (stored) {
        const data = await loadAttempt(stored).catch(() => null);
        // Completed attempts are never reopened from the public route: the pointer is stale → fresh intro.
        // (Normal completion already clears it; this covers legacy/partial pointers.)
        if (data && data.simulation_id === sim.simulationId && data.status === "completed") {
          clearStoredSession();
          setIntro(true);
          setScreen(FIRST_SCREEN);
          return;
        }
        if (data && data.simulation_id === sim.simulationId && data.status === "started") {
          const cur = data.state?.current_screen;
          const known = SCREENS[cur] || D[cur] || E[cur] || SUPPORT_SCREENS.has(cur) || cur === END_SCREEN || (cur >= REFLECTION_START && cur <= REFLECTION_END);
          setSid(stored);
          setState(data.state || {});
          setScreen(known ? cur : FIRST_SCREEN);
          if (known && cur !== FIRST_SCREEN) { stageSent.current = true; appendEvent(stored, "attempt_resumed", cur); }
          if (known && cur >= 9) {
            if (cur >= 13) stages.current.add(BLOCK_2_STAGE);
            if (cur >= 21 && cur !== FINAL_CONTACT_SCREEN) stages.current.add(BLOCK_3_STAGE);
            if (cur >= PHASE_E_ENTRY) stages.current.add(BLOCK_4_STAGE);
            if (cur >= REFLECTION_START) stages.current.add(BLOCK_5_STAGE);
            for (const ev of data.events || []) {
              if (ev.name === "contact_ignored") ignores.current += 1;
              else if (ENGAGE_EVENTS.has(ev.name)) ignores.current = 0;
              else if (ev.name === "final_contact_opportunity_presented") finalShown.current = true;
            }
          }
          return;
        }
        clearStoredSession();
      }
      setIntro(true);
      setScreen(FIRST_SCREEN);
    })();
  }, [sim.simulationId]);

  // BEGIN = the single canonical start: exactly one session, one sim_start; then Screen 1.
  const begin = useCallback(async () => {
    if (busy || sid) return;
    setBusy(true);
    unlock();
    const s = await startAttempt(sim, replayNext.current);
    replayNext.current = false;
    setSid(s);
    setIntro(false);
    setBusy(false);
  }, [busy, sid, sim, unlock]);

  // Ambience follows the world (shared synthesized "outdoor" profile; graceful when audio blocked).
  useEffect(() => {
    if (!SCREENS[screen]) return;
    try { setAmbientScene(SCREENS[screen].env === "title" ? "calm" : "outdoor"); } catch {}
  }, [screen, setAmbientScene]);

  // Authored multiplayer feed: lines arrive with a natural rhythm; never generated.
  useEffect(() => {
    if (!SCREENS[screen]) return;
    const lines = SCREENS[screen].feed || [];
    setFeed([]);
    const timers = lines.map((l, i) => setTimeout(() => { setFeed((f) => [...f, l]); try { chime(); } catch {} }, reduce ? 200 : 900 + i * 1400));
    return () => timers.forEach(clearTimeout);
  }, [screen, reduce, chime]);

  const act = useCallback(async (action) => {
    if (busy || !screen || !isValidTransition(screen, action.id)) return; // invalid transitions fail safely
    setBusy(true);
    try { click(); } catch {}
    let current = sid;
    if (!current) { unlock(); current = await startAttempt(sim); setSid(current); }
    if (action.event) appendEvent(current, action.event, screen, `-${action.id}`);
    if (!stageSent.current) { stageSent.current = true; trackStage(sim, current, BLOCK_1_STAGE); }
    if (action.next) {
      const patch = { current_screen: action.next };
      setState((s) => ({ ...s, ...patch }));
      await saveState(current, patch);
      appendEvent(current, "screen_entered", action.next);
      setScreen(action.next);
    }
    setBusy(false);
  }, [busy, screen, sid, sim, unlock, click]);

  const stage = useCallback((key) => { if (!stages.current.has(key)) { stages.current.add(key); trackStage(sim, sid, key); } }, [sim, sid]);

  const enter = useCallback((next) => {
    let target = next === "return" ? (returnTo.current || 15) : next;
    if (state.safeguarding_override && target >= END_SCREEN) return; // paused attempts are terminal: never reach reflection
    if (target === REFLECTION_START) stage(BLOCK_5_STAGE);
    // Option B: after repeated non-engagement, ONE natural gameplay-created opportunity; then respect the choice.
    if (isContactScreen(target) && ignores.current >= OPTION_B_THRESHOLD) {
      if (!finalShown.current) {
        finalShown.current = true; returnTo.current = target;
        appendEvent(sid, "final_contact_opportunity_presented", FINAL_CONTACT_SCREEN);
        target = FINAL_CONTACT_SCREEN;
      } else {
        // Contact has ended; Sam17 stays another player on the map and normal gameplay completion continues (Phase E route).
        appendEvent(sid, "contact_ended_by_non_engagement", target);
        target = NORMALITY_SCREEN;
      }
    }
    if (target === 13) stage(BLOCK_2_STAGE);
    if (target === 21) stage(BLOCK_3_STAGE);
    if (BLOCK_4_ENTRIES.has(target)) stage(BLOCK_4_STAGE);
    if (target === NORMALITY_SCREEN && state.help_sought !== true) { setState((s) => ({ ...s, help_sought: false })); saveState(sid, { help_sought: false }); }
    if (target === 47) { setState((s) => ({ ...s, help_sought: true })); saveState(sid, { help_sought: true }); }
    if (target === END_SCREEN) appendEvent(sid, "lived_scenario_completed", END_SCREEN); // idempotent event_id => exactly once per attempt
    setState((s) => ({ ...s, current_screen: target }));
    saveState(sid, { current_screen: target });
    appendEvent(sid, "screen_entered", target);
    setScreen(target);
  }, [sid, stage, state.help_sought, state.safeguarding_override]);

  const engine = useMemo(() => ({
    patch: async (p) => { setState((s) => ({ ...s, ...p })); await saveState(sid, p); },
    event: (name, scr, suffix = "") => appendEvent(sid, name, scr, suffix),
    ignore: () => { ignores.current += 1; },
    resetIgnores: () => { ignores.current = 0; },
    pause: async () => { setState((s) => ({ ...s, safeguarding_override: true })); await saveState(sid, { safeguarding_override: true }); },
    handoff: (reason) => enter(HANDOFF_ROUTES[reason] ?? NORMALITY_SCREEN),
    goto: enter,
    // Screen 80 → record → build (create-once; server emits simulation_complete on first insert) → render.
    // A completed attempt is no longer resumable: only the sim_000002 pointer is cleared (report/history stay server-side).
    complete: async () => {
      await recordCompletion(sid, sim);
      const rep = await buildReport(sid);
      clearStoredSession();
      setReport(rep);
      return rep;
    },
    // Fresh attempt: new session_id, clean state; the finished attempt/report/history are untouched.
    practiseAgain: async (fromScreen) => {
      appendEvent(sid, "practise_again_selected", fromScreen);
      clearStoredSession(); setReport(null);
      ignores.current = 0; finalShown.current = false; stages.current = new Set(); stageSent.current = false; returnTo.current = null;
      setState({}); setFeed([]);
      // Fresh-intro state: no new attempt until BEGIN (begin() emits simulation_restart for this replay).
      replayNext.current = true;
      setSid(null); setIntro(true); setScreen(FIRST_SCREEN);
    },
  }), [sid, enter, sim]);

  // Safeguarding is terminal: no Sam17 messages, no reflection, no report, no feedback, no completion.
  if (state.safeguarding_override) return <SafeguardingClose onClose={clearStoredSession} />;
  if (screen === null) return <Shell><p className="text-white/40 text-sm">Loading…</p></Shell>;
  if (intro && !sid) return <IntroScreen onBegin={begin} busy={busy} />;
  if (report) return <PracticeReportV2 summary={report} simTitle={sim.title} simId={sim.simulationId} sessionId={sid} onHome={() => navigate("/")} onRestart={() => engine.practiseAgain(COMPLETE_SCREEN)} />;
  if (screen === END_SCREEN || (screen >= REFLECTION_START && screen <= REFLECTION_END)) return <ReflectionStage key={screen} sid={sid} screen={screen} state={state} engine={engine} />;
  if (SUPPORT_SCREENS.has(screen)) return <SupportStage key={screen} sid={sid} screen={screen} state={state} engine={engine} />;
  if (D[screen] || E[screen]) return <PhaseDStage key={screen} sid={sid} screen={screen} state={state} engine={engine} />;

  const def = SCREENS[screen];
  const players = def.env === "title" ? [] : PLAYERS.filter((n) => (screen >= 6 ? true : n !== "Sam17")).slice(0, screen >= 6 ? 3 : 2);
  return (
    <div className="relative h-[100dvh] w-full overflow-hidden bg-[#0a0a0a] text-white" data-testid="wildrush-experience" style={{ paddingTop: "env(safe-area-inset-top)" }}>
      <AnimatePresence mode="wait">
        <motion.div key={def.env + screen} className="absolute inset-0" initial={{ opacity: 0, scale: reduce ? 1 : 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }} transition={{ duration: reduce ? 0.2 : 0.9, ease: [0.22, 1, 0.36, 1] }}>
          <WildRushWorld env={def.env} players={players} />
        </motion.div>
      </AnimatePresence>

      {/* HUD */}
      <div className="absolute inset-x-0 top-0 z-20 flex items-start justify-between p-4 sm:p-6" data-testid="wr-hud">
        <div className="flex items-center gap-2">
          <WildRushHome />
          <div className="whitespace-nowrap rounded-full border border-white/15 bg-black/35 px-3.5 py-1.5 text-xs font-bold tracking-[0.22em] backdrop-blur" data-testid="wr-brand">WILD RUSH</div>
        </div>
        <div className="flex items-center gap-2">
          {def.objective && (
            <div className="flex items-center gap-2 rounded-full border border-white/20 bg-[#06131a]/55 px-3.5 py-1.5 text-[13px] font-medium text-white backdrop-blur" data-testid="wr-objective" title={def.objective} aria-label={def.objective}>
              <MapPin size={14} style={{ color: ACCENT }} /><span className="hidden min-[420px]:inline">{def.objective}</span>
            </div>
          )}
          <button onClick={toggle} aria-label={enabled ? "Sound on" : "Sound off"} data-testid="wr-sound" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/15 bg-black/35 backdrop-blur">
            {enabled ? <Volume2 size={15} style={{ color: ACCENT }} /> : <VolumeX size={15} className="text-white/50" />}
          </button>
        </div>
      </div>

      {/* In-game feed (authored, deterministic) */}
      <div className="absolute left-4 right-4 top-20 z-20 flex flex-col gap-2 sm:left-6 sm:max-w-sm" data-testid="wr-feed" aria-live="polite">
        <AnimatePresence>
          {feed.map((l, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} className="w-fit rounded-2xl border border-white/10 bg-[#06131a]/60 px-3.5 py-2 text-[15px] leading-snug shadow-[0_2px_10px_rgba(0,0,0,.18)] backdrop-blur" data-testid={`wr-feed-${l.who}`}>
              <span className="mr-2 font-semibold text-[#9fe0c0]">{l.who}</span><span className="text-white">{l.text}</span>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Bottom: caption + in-world actions */}
      <div className="absolute inset-x-0 bottom-0 z-20 p-5 sm:p-8" style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}>
        <div className="mx-auto max-w-lg">
          {def.title && (
            <div className="mb-6 text-center">
              <p className="font-display text-5xl font-bold tracking-tight" data-testid="wr-title">{def.title}</p>
              <p className="mt-2 text-sm font-medium text-white/90 [text-shadow:0_1px_3px_rgba(0,0,0,0.45)]" data-testid="wr-sub">{def.sub}</p>
            </div>
          )}
          {def.caption && <p className="mb-4 text-[16px] leading-relaxed text-white drop-shadow-[0_1px_2px_rgba(0,0,0,.6)]" data-testid="wr-caption">{def.caption}</p>}
          <div className="flex flex-col gap-3 sm:flex-row">
            {def.actions.map((a) => (
              <button key={a.id} onClick={() => act(a)} disabled={busy} data-testid={`wr-action-${a.id}`}
                className="inline-flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-full border border-white/25 bg-white/12 px-6 text-[15px] font-semibold backdrop-blur transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#4FA3D9] focus-visible:ring-offset-2 focus-visible:ring-offset-[#06131a] disabled:opacity-50">
                <Compass size={15} style={{ color: ACCENT }} />{a.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const Shell = ({ children }) => (
  <div className="flex h-[100dvh] w-full items-center justify-center bg-[#0a0a0a] px-6" data-testid="wildrush-shell">{children}</div>
);
